import { useState } from 'react';
import { useContactInfo } from '../hooks/usePublicContent';
import { Container } from '../components/ui/Container';
import { Eyebrow } from '../components/ui/Eyebrow';

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
    </svg>
  );
}

export function ContactPage() {
  const { data, isLoading } = useContactInfo();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // mailto fallback — replace with real API when ready
    const mailto = `mailto:${data?.email ?? ''}?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(`Name: ${form.name}\n\n${form.message}`)}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  const mapSrc = data?.map_embed
    ? data.map_embed.trim().startsWith('<')
      ? null   // raw HTML embed — rendered via dangerouslySetInnerHTML
      : data.map_embed  // plain URL — used as iframe src
    : null;

  return (
    <div>
      {/* ── Hero ── */}
      <section className="bg-ink py-20 md:py-28">
        <Container>
          <Eyebrow dark>Get in touch</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-light text-cream md:text-6xl">
            Contact Us
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-cream/60">
            Questions about an order, a custom print, or just want to say hello?
            We're here — and we actually reply.
          </p>
        </Container>
      </section>

      {/* ── Info cards ── */}
      <section className="border-b border-stone-light bg-paper py-14">
        <Container>
          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-32 animate-pulse rounded-lg bg-stone-light/30" />
              ))}
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {data?.address && (
                <div className="flex flex-col gap-3 rounded-lg border border-stone-light p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-cream">
                    <PinIcon />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">Address</p>
                  <p className="text-sm leading-relaxed text-ink">{data.address}</p>
                </div>
              )}
              {data?.phone && (
                <div className="flex flex-col gap-3 rounded-lg border border-stone-light p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-cream">
                    <PhoneIcon />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">Phone</p>
                  <a href={`tel:${data.phone}`} className="text-sm text-ink transition-colors hover:text-clay">
                    {data.phone}
                  </a>
                </div>
              )}
              {data?.email && (
                <div className="flex flex-col gap-3 rounded-lg border border-stone-light p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-cream">
                    <EmailIcon />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">Email</p>
                  <a href={`mailto:${data.email}`} className="text-sm text-ink transition-colors hover:text-clay break-all">
                    {data.email}
                  </a>
                </div>
              )}
              {data?.working_hours && (data.working_hours.sun_thu || data.working_hours.fri_sat) && (
                <div className="flex flex-col gap-3 rounded-lg border border-stone-light p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-cream">
                    <ClockIcon />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-stone">Hours</p>
                  <div className="space-y-1 text-sm text-ink">
                    {data.working_hours.sun_thu && <p>Sun–Thu · {data.working_hours.sun_thu}</p>}
                    {data.working_hours.fri_sat && <p>Fri–Sat · {data.working_hours.fri_sat}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </Container>
      </section>

      {/* ── Map + Form ── */}
      <section className="py-16 md:py-20">
        <Container>
          <div className="grid gap-12 md:grid-cols-2">

            {/* Contact form */}
            <div>
              <h2 className="font-serif text-2xl font-light text-ink md:text-3xl">Send us a message</h2>
              <p className="mt-2 text-sm text-stone">We'll get back to you within one business day.</p>

              {submitted ? (
                <div className="mt-8 rounded-lg border border-stone-light bg-paper px-6 py-8 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ink">
                    <svg className="h-6 w-6 text-cream" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10l4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p className="mt-4 font-serif text-xl font-light text-ink">Message sent</p>
                  <p className="mt-2 text-sm text-stone">Your email client should open shortly.</p>
                  <button onClick={() => setSubmitted(false)} className="mt-5 text-sm text-stone underline decoration-stone-light hover:text-ink">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-stone mb-2">Name</span>
                      <input
                        required
                        type="text"
                        value={form.name}
                        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Your name"
                        className="w-full border border-stone-light bg-white px-4 py-3 text-sm text-ink placeholder:text-stone/50 focus:border-ink focus:outline-none"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-stone mb-2">Email</span>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="your@email.com"
                        className="w-full border border-stone-light bg-white px-4 py-3 text-sm text-ink placeholder:text-stone/50 focus:border-ink focus:outline-none"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-stone mb-2">Subject</span>
                    <input
                      required
                      type="text"
                      value={form.subject}
                      onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                      placeholder="Order enquiry, custom print, etc."
                      className="w-full border border-stone-light bg-white px-4 py-3 text-sm text-ink placeholder:text-stone/50 focus:border-ink focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-stone mb-2">Message</span>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                      placeholder="Tell us how we can help…"
                      className="w-full resize-none border border-stone-light bg-white px-4 py-3 text-sm text-ink placeholder:text-stone/50 focus:border-ink focus:outline-none"
                    />
                  </label>
                  <button
                    type="submit"
                    className="w-full bg-ink px-6 py-4 text-[12px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-clay sm:w-auto sm:px-10"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Map */}
            <div className="flex flex-col gap-4">
              <h2 className="font-serif text-2xl font-light text-ink md:text-3xl">Find us</h2>
              {data?.map_embed ? (
                data.map_embed.trim().startsWith('<') ? (
                  <div
                    className="h-80 overflow-hidden rounded-lg border border-stone-light md:h-full md:min-h-[400px] [&_iframe]:h-full [&_iframe]:w-full"
                    dangerouslySetInnerHTML={{ __html: data.map_embed }}
                  />
                ) : (
                  <iframe
                    src={mapSrc ?? ''}
                    title="Location map"
                    className="h-80 w-full rounded-lg border border-stone-light md:h-full md:min-h-[400px]"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                )
              ) : (
                <div className="flex h-80 items-center justify-center rounded-lg border border-dashed border-stone-light bg-paper text-center md:h-full md:min-h-[400px]">
                  <div>
                    <PinIcon />
                    <p className="mt-3 text-sm text-stone">Map not configured.</p>
                    <p className="mt-1 text-xs text-stone/60">Add a Google Maps embed in the admin under Contact Info.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
